import { Role } from 'auth';
import { get } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import {
  useCurrentUserRegistersQuery,
  useGetRegisterClassItem,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { ClassNAV } from '@/modules/issuer-dashboard/components/class-tabs/ClassNAV.tsx';
import { ClassOverview } from '@/modules/issuer-dashboard/components/class-tabs/ClassOverview.tsx';
import { IssuerAddDistributionModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddDistributionModal.tsx';
import { IssuerInviteShareholderModal } from '@/modules/issuer-dashboard/components/modals/IssuerInviteShareholderModal.tsx';
import IssuerEditClassModal, {
  IssuerUpdateClassModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerUpdateClassModal.tsx';
import {
  BuyBacksBody,
  HeaderAddBuyBack,
} from '@/modules/issuer-dashboard/pages/IssuerBuyBackNew.tsx';
import { CertificateBody } from '@/modules/issuer-dashboard/components/certificates/CertificateBody.tsx';
import { DistributionsEventsBody } from '@/modules/issuer-dashboard/pages/IssuerDistributionsEvents.tsx';
import { HeaderHolders, HoldersBody } from '@/modules/issuer-dashboard/pages/IssuerHoldersNew.tsx';
import {
  TransactionsBody,
  TransactionsHeaderAction,
} from '@/modules/issuer-dashboard/pages/IssuerTransactions.tsx';
import { TransfersBody } from '@/modules/issuer-dashboard/pages/IssuerTransfers.tsx';
import { useTopHoldersQuery } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Button, ModalProviderContext, PageLayout, Skeleton, Spinner, TabItem, Tabs } from 'ui';
import { useIsMobile } from 'ui/hooks';
import { ArrowLeftIcon, UsersAddGrayIcon } from 'ui/icons';
import { RegisterClassResponse, RegisterResponse, RegisterType } from 'api';

enum TabsEnum {
  Overview = 'Overview',
  NAV = 'NAV',
  Transactions = 'Transactions',
  Transfers = 'Transfers',
  Distributions = 'Distributions',
  'Buy backs' = 'Buy backs',
  Certificates = 'Certificates',
  Holders = 'Holders',
}

export const CLASS_TAB_NAME = 'tab-class';

const IssuerClass = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const paramTab = params.get(CLASS_TAB_NAME);
  const isValidTab = paramTab && Object.values(TabsEnum).includes(paramTab as TabsEnum);
  const [activeTab, setActiveTab] = useState<TabsEnum>(
    isValidTab ? (paramTab as TabsEnum) : TabsEnum.Overview,
  );

  const { data, isLoading: isLoadingRegisterClass } = useGetRegisterClassItem(
    (id && +id) || 0,
    !id || isNaN(+id),
  );
  const { error: errorTopHolders } = useTopHoldersQuery(
    { register_class_id: (id && +id) || undefined },
    false,
  );
  const { data: registers } = useCurrentUserRegistersQuery();
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const register = params.get(REGISTER_ID_SEARCH_PARAM_KEY) || registerId || '';

  const currentRegister = useMemo(
    () =>
      (registers?.data || []).find(({ register_id }) => register_id === +register)?.register_obj,
    [register, registers?.data],
  );

  const title = (
    <div className="flex gap-3 items-center">
      <button onClick={navigate.bind(null, state?.from || -1)}>
        <img src={ArrowLeftIcon} alt="back" className="w-6 h-6 min-w-6 min-h-6" />
      </button>
      {isLoadingRegisterClass ? (
        <div className="flex flex-col gap-1">
          <Skeleton width={250} height={28} />
          <Skeleton width={140} height={16} />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="text-xl font-[RecifeDisplay] font-[600]">
            {data?.register_class?.class_name}
          </span>
          <span className="text-xs text-neutral-500 font-[Helvetica,Arial,sans-serif]">
            Created at{' '}
            {dateToPrint(data?.register_class?.created_at, DateVariant.Information, true) || '-'}
          </span>
        </div>
      )}
    </div>
  );

  const tabsItems = useMemo<TabItem<TabsEnum>[]>(
    () =>
      [
        {
          name: TabsEnum.Overview,
          label: 'Overview',
          container: data?.register_class ? (
            <ClassOverview classItem={data?.register_class} isLoading={isLoadingRegisterClass} />
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
        },
        {
          name: TabsEnum.NAV,
          label: 'NAV',
          container: data?.register_class ? (
            <ClassNAV classItem={data?.register_class} isLoading={isLoadingRegisterClass} />
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
        },
        {
          name: TabsEnum.Transactions,
          label: 'Transactions',
          container: data?.register_class ? (
            <div className="w-full py-2">
              <TransactionsBody
                hideChooseRegisterClass={true}
                register_class_id={data?.register_class?.id}
                rightBlock={<TransactionsHeaderAction />}
              />
            </div>
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
        },
        {
          name: TabsEnum.Transfers,
          label: 'Transfers',
          container: data?.register_class ? (
            <div className="w-full py-2">
              <TransfersBody
                hideChooseRegisterClass={true}
                register_class_id={data?.register_class?.id}
                rightBlock={<TransactionsHeaderAction buttonName="Add Transfer" />}
              />
            </div>
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
        },
        {
          name: TabsEnum.Distributions,
          label: 'Distributions',
          container: data?.register_class ? (
            <div className="w-full py-2">
              <DistributionsEventsBody
                hideChooseRegisterClass={true}
                register_class_id={data?.register_class?.id}
              />
            </div>
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
        },
        {
          name: TabsEnum['Buy backs'],
          label: 'Buy backs',
          container: data?.register_class ? (
            <div className="w-full py-2">
              <BuyBacksBody
                register_class_id={data?.register_class?.id}
                rightBlock={<HeaderAddBuyBack />}
                hideChooseRegisterClass={true}
              />
            </div>
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
          disabled: currentRegister?.type !== RegisterType.Company,
          disabledReason: 'Available only for company',
        },
        {
          name: TabsEnum.Certificates,
          label: 'Certificates',
          container: data?.register_class ? (
            <div className="w-full py-2">
              <CertificateBody
                hideChooseRegisterClass={true}
                register_class_id={data?.register_class?.id}
              />
            </div>
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
        },
        {
          name: TabsEnum.Holders,
          label: 'Holders',
          container: data?.register_class ? (
            <div className="w-full py-2">
              <HoldersBody
                register_class_id={data?.register_class?.id}
                rightBlock={<HeaderHolders />}
              />
            </div>
          ) : (
            <div className="flex flex-col w-full h-[250px] justify-center items-center">
              <Spinner size="lg" />
            </div>
          ),
        },
      ].filter(
        item =>
          item.label !== 'Buy backs' ||
          (item.label === 'Buy backs' && currentRegister?.type === RegisterType.Company),
      ),
    [currentRegister, data?.register_class, isLoadingRegisterClass],
  );

  if (get(errorTopHolders, 'response.status') === 400) {
    // toast.error('Specified register class ID not found in your register', { id: 'class' });
    return (
      <Navigate to={{ pathname: issuerDashboardPaths.static.classes, search: params.toString() }} />
    );
  }
  return (
    <PageLayout
      title={title}
      setActiveTab={setActiveTab}
      tabNameParam={CLASS_TAB_NAME}
      activeTab={activeTab}
      tabs={tabsItems}
      contentClassName="px-0 py-0"
      headerAction={
        <HeaderAction
          classItem={data?.register_class}
          isLoading={isLoadingRegisterClass}
          currentRegister={currentRegister}
        />
      }
    >
      <Tabs activeTab={activeTab} items={tabsItems} />
    </PageLayout>
  );
};

interface HeaderActionProps {
  classItem?: RegisterClassResponse;
  currentRegister?: RegisterResponse;
  isLoading: boolean;
}

const HeaderAction = ({ classItem, isLoading, currentRegister }: HeaderActionProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { isMobile } = useIsMobile();
  const {
    class_name,
    id,
    register_id,
    current_price_per_share,
    min_investment,
    transfer_lock_period,
  } = classItem || {};
  const { name } = currentRegister || {};
  const handleOpenInviteShareholderModal = useCallback(() => {
    if (!register_id || !class_name || !id || !name) return;
    openModal(IssuerInviteShareholderModal, {
      defaultRegisterClassOption: {
        label: class_name,
        value: {
          id,
          current_price_per_share: current_price_per_share ?? 0,
          toString: () => class_name,
        },
      },
      defaultRegisterOption: {
        label: name || 'register name',
        value: { id: register_id, toString: () => name || 'register name' },
      },
      disabledSubscription: current_price_per_share === 0,
    });
  }, [class_name, current_price_per_share, id, name, openModal, register_id]);

  const handleOpenDistributionModal = useCallback(() => {
    if (!register_id || !class_name || !id || !registerCountry || !current_price_per_share) return;
    openModal(IssuerAddDistributionModal, {
      registerCountry: registerCountry,
      register_id: +register_id,
      selectedClass: {
        label: class_name,
        value: {
          id: id,
          price_per_share: current_price_per_share,
          toString: () => class_name,
        },
      },
    });
  }, [openModal, register_id, class_name, id, registerCountry, current_price_per_share]);

  const handleOpenEditClassModal = useCallback(() => {
    if (!class_name || !id || !current_price_per_share || !min_investment) return;
    openModal<IssuerUpdateClassModalProps>(IssuerEditClassModal, {
      id,
      class_name,
      min_investment,
      transfer_lock_period,
      current_price_per_share,
    });
  }, [class_name, id, current_price_per_share, min_investment, transfer_lock_period, openModal]);

  if (isLoading || !classItem || !currentRegister) {
    return (
      <div className="flex items-center gap-4">
        <Skeleton width={70} height={28} />
        <Skeleton width={70} height={28} />
        <Skeleton width={70} height={28} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <Button
        className="w-[165px] px-0 py-0 min-h-8"
        variant="secondary"
        onClick={handleOpenDistributionModal}
      >
        Add new {getRegisterTypeStrings('dividend')}
      </Button>

      <Button
        className="w-[70px] px-0 py-0 min-h-8"
        variant="secondary"
        onClick={handleOpenEditClassModal}
      >
        Edit
      </Button>

      {isMobile ? (
        <span onClick={handleOpenInviteShareholderModal}>
          <img
            alt="Add holders"
            src={UsersAddGrayIcon}
            className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
          />
        </span>
      ) : (
        <Button
          className="w-[165px] px-0 py-0 min-h-8"
          variant="primary"
          onClick={handleOpenInviteShareholderModal}
        >
          Get a {getRegisterTypeStrings('application')} link
        </Button>
      )}
    </div>
  );
};

export default withPrivateAccess(IssuerClass, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
